/**
 * Which saved reports a click may land on — DERIVED, never configured.
 *
 * Nobody picks drill targets. A report qualifies for a clicked cell when it can
 * answer the question that cell asks and would show the reader something the
 * cell does not already say. That is decided here, per click, in the browser —
 * because what a cell PINS varies across one report: a data cell pins both
 * axes, a row total only the rows, the grand total nothing. Every rule below
 * gets EASIER as a cell pins less, so a list resolved once for the most-pinned
 * cell would silently lose targets that are perfectly good for a total.
 *
 * The server does the part that does not vary by cell and that only it may
 * do: which reports the viewer can see at all (rule 1), whether the owner kept
 * one out of drill menus, and whether the viewer may read its model. See
 * `GET /api/report-definitions/drill-targets`.
 *
 * See docs/reporting/drill-down.md.
 */
import { canFilterField, findDimension, findMeasure, type ReportModel } from './model';
import type { ReportKind } from './spec';

/**
 * How deep one chain of drills may go before the menu stops offering more.
 *
 * Rule 4 already keeps a report from appearing twice in one chain, so a cycle
 * cannot be walked into from a menu. This is the belt to that: a report can be
 * edited after a link to a chain was shared, and a bookmarked chain is replayed
 * against whatever the reports say NOW.
 */
export const MAX_DRILL_DEPTH = 6;

/** A saved report as a drill menu sees it. What the endpoint returns. */
export interface DrillCandidate {
  id: string;
  /** What the menu prints — the owner's short label, else the name. */
  label: string;
  /** The full name, for a tooltip when the label is a shortening of it. */
  name: string;
  model: string;
  kind: ReportKind;
  /** Rows then columns, outermost first. Empty for a listing. */
  axes: readonly string[];
  /** Dimensions the report's OWN filters name — which a drill drops. */
  filterDimensions: readonly string[];
}

/** What a click brings to the menu. */
export interface DrillOrigin {
  /** The model of the report the click was made on. */
  model: ReportModel;
  /** Dimension ids the cell pins, `null`-keyed ones included. */
  pinned: readonly string[];
  /** Dimension ids the report's own filters name. They travel too. */
  filtered: readonly string[];
  /** Saved-report ids already in this chain, the report clicked on included. */
  chain: readonly string[];
}

export type Eligibility = { ok: true } | { ok: false; reason: string };

/**
 * The determinants that decide `dimensionId`, if every one is pinned.
 *
 * Read from the dimension's own `attributeOf`. `null` when it declares none or
 * one of them is still free.
 */
export function determinedBy(
  model: Pick<ReportModel, 'dimensions'>,
  dimensionId: string,
  pinned: ReadonlySet<string>,
): readonly string[] | null {
  const of = findDimension(model, dimensionId)?.attributeOf;
  if (!of?.length) return null;
  return of.every((id) => pinned.has(id)) ? of : null;
}

/**
 * May `candidate` be offered for this click, and if not, why not.
 *
 * `models` is every model the viewer may read. The reason is written for the
 * reader — the menu's "why only these?" panel prints it verbatim.
 */
export function drillEligibility(
  models: readonly ReportModel[],
  origin: DrillOrigin,
  candidate: DrillCandidate,
): Eligibility {
  // Rule 4. A report already in the chain would show the reader a view they
  // came through — and is the only way a menu could walk into a cycle.
  if (origin.chain.includes(candidate.id)) {
    return { ok: false, reason: 'Already open in this drill.' };
  }
  if (origin.chain.length >= MAX_DRILL_DEPTH) {
    return { ok: false, reason: `A drill goes at most ${MAX_DRILL_DEPTH} levels deep.` };
  }
  const target = models.find((m) => m.id === candidate.model);
  if (!target) return { ok: false, reason: 'Its subject area is not available to you.' };

  const label = (id: string) =>
    findDimension(origin.model, id)?.label ??
    findMeasure(origin.model, id)?.label ??
    findDimension(target, id)?.label ??
    id;
  const list = (ids: readonly string[]) => ids.map(label).join(', ');
  // A dimension OR a measure with a per-row value: a source report may filter
  // on either, and both have to reach the landing for its rows to be the ones
  // behind the figure.
  const filterable = (id: string) => canFilterField(target, id);
  const pinned = new Set(origin.pinned);

  // Rule 2. Every pinned dimension must travel — or be decided by ones that
  // DO travel. Pinned is not enough: Tenure is decided by Employee only on a
  // landing that Employee reaches, so the determinants have to arrive too. A
  // fixed point, because an attribute's determinant may itself be carried only
  // as an attribute of something else.
  const carried = new Set(origin.pinned.filter(filterable));
  for (let grew = true; grew; ) {
    grew = false;
    for (const id of origin.pinned) {
      if (!carried.has(id) && determinedBy(origin.model, id, carried)) {
        carried.add(id);
        grew = true;
      }
    }
  }
  const unpinnable = origin.pinned.filter((id) => !carried.has(id));
  // A report's own filters travel as well, and nothing decides a filter that
  // may hold several values, so those must be filterable outright.

  const unfilterable = origin.filtered.filter((id) => !filterable(id) && !pinned.has(id));
  if (unpinnable.length || unfilterable.length) {
    const missing = [...new Set([...unpinnable, ...unfilterable])];
    return {
      ok: false,
      reason: `The ${target.label} model cannot be filtered by ${list(missing)}.`,
    };
  }

  // A listing is always a valid landing: it has no axes to add, it IS the
  // rows — in the layout its author chose.
  if (candidate.kind === 'detail') return { ok: true };

  // Rule 3. It must break the figure down by something the cell has not
  // already decided. An axis that is an attribute of what is pinned would
  // print one row — Tenure, once the employee is known.
  const decided: string[] = [];
  for (const axis of candidate.axes) {
    if (pinned.has(axis)) continue;
    const by = determinedBy(target, axis, pinned) ?? determinedBy(origin.model, axis, pinned);
    if (!by) return { ok: true };
    decided.push(`${label(axis)} is an attribute of ${list(by)}`);
  }
  return {
    ok: false,
    reason: decided.length
      ? `Adds only what is already decided — ${decided.join('; ')}. It would show one row.`
      : 'Every axis it groups by is already pinned. It would show one row.',
  };
}

/**
 * The menu's order: (a) reports whose LEADING axis is the next level down from
 * something the click pinned — the dimension-level `drillTo` chain is already
 * a hierarchy (`countryGroup → srController → employee`); (b) reports on the
 * same model; (c) the rest. Deeper matches first within (a), since the
 * innermost pinned level is the one the reader was looking at.
 */
export function rankDrillTargets<C extends DrillCandidate>(
  origin: Pick<DrillOrigin, 'model' | 'pinned'>,
  candidates: readonly C[],
): C[] {
  const nextLevel = new Map<string, number>();
  for (const [depth, id] of origin.pinned.entries()) {
    const next = findDimension(origin.model, id)?.drillTo?.dimension;
    if (next) nextLevel.set(next, depth);
  }
  const score = (c: C): [number, number] => {
    const lead = c.axes[0];
    const depth = lead === undefined ? undefined : nextLevel.get(lead);
    if (depth !== undefined) return [0, -depth];
    return [c.model === origin.model.id ? 1 : 2, 0];
  };
  return [...candidates].sort((a, b) => {
    const [ta, da] = score(a);
    const [tb, db] = score(b);
    return ta - tb || da - db || a.label.localeCompare(b.label);
  });
}

/** Dimension ids a spec's filters name, groups opened. */
export function filterDimensionIds(
  filters: readonly ({ field: string } | { children: readonly { field: string }[] })[],
): string[] {
  return [
    ...new Set(
      filters.flatMap((n) => ('children' in n ? n.children.map((r) => r.field) : [n.field])),
    ),
  ];
}
